import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// AddProjectModal picks an owner + repo from the GitHub APIs and
// POSTs to /api/projects. Built on shadcn/ui primitives only —
// see AGENTS.md for the rule.
//
// Behavior:
//   * Owners load on open (small list — user + their orgs).
//   * Repos load on owner change and are filtered client-side by
//     a substring against names (cheaper than the /search rate-limit
//     bucket).
//   * Add is disabled until both selections are made.
export function AddProjectModal({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAdded: (created: { owner: string; name: string }) => void;
}) {
  const [owners, setOwners] = useState<string[]>([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [ownersError, setOwnersError] = useState<string | null>(null);

  const [owner, setOwner] = useState("");

  const [repos, setRepos] = useState<string[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [reposError, setReposError] = useState<string | null>(null);
  const [repo, setRepo] = useState("");
  const [filter, setFilter] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // loadOwners fetches the cached list. When `refresh` is true the
  // backend forces an upstream re-fetch first (POST .../refresh),
  // which is what the "↻" icon next to the Owner select triggers.
  const loadOwners = useCallback(async (refresh: boolean) => {
    setLoadingOwners(true);
    setOwnersError(null);
    try {
      const res = refresh
        ? await fetch("/api/github/owners/refresh", { method: "POST" })
        : await fetch("/api/github/owners");
      if (!res.ok) throw new Error(await res.text());
      setOwners(await res.json());
    } catch (e) {
      setOwnersError(humanError(e));
    } finally {
      setLoadingOwners(false);
    }
  }, []);

  const loadRepos = useCallback(
    async (forOwner: string, refresh: boolean) => {
      if (!forOwner) {
        setRepos([]);
        return;
      }
      setLoadingRepos(true);
      setReposError(null);
      try {
        const res = refresh
          ? await fetch(`/api/github/repos/${encodeURIComponent(forOwner)}/refresh`, {
              method: "POST",
            })
          : await fetch(`/api/github/repos?owner=${encodeURIComponent(forOwner)}`);
        if (!res.ok) throw new Error(await res.text());
        setRepos(await res.json());
      } catch (e) {
        setReposError(humanError(e));
      } finally {
        setLoadingRepos(false);
      }
    },
    [],
  );

  // Reset state every time the modal opens so a previous run's
  // selection doesn't bleed in.
  useEffect(() => {
    if (!open) return;
    setOwner("");
    setRepo("");
    setFilter("");
    setRepos([]);
    setSubmitError(null);
    void loadOwners(false);
  }, [open, loadOwners]);

  // Load repos when owner changes.
  useEffect(() => {
    setRepo("");
    setFilter("");
    void loadRepos(owner, false);
  }, [owner, loadRepos]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter((r) => r.toLowerCase().includes(q));
  }, [repos, filter]);

  const canAdd = !!owner && !!repo && !submitting;

  const submit = useCallback(async () => {
    if (!canAdd) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ owner, name: repo }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      onAdded({ owner, name: repo });
      onOpenChange(false);
    } catch (e) {
      setSubmitError(humanError(e));
    } finally {
      setSubmitting(false);
    }
  }, [canAdd, owner, repo, onAdded, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add project</DialogTitle>
          <DialogDescription>
            Pick a repo bee should watch. The list is scoped to repos
            your GitHub user can access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="owner">Owner</Label>
            {loadingOwners ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading owners…
              </div>
            ) : ownersError ? (
              <p className="text-sm text-destructive">{ownersError}</p>
            ) : (
              <div className="flex items-center gap-2">
                <Select value={owner} onValueChange={setOwner}>
                  <SelectTrigger id="owner" className="flex-1">
                    <SelectValue placeholder="Select owner…" />
                  </SelectTrigger>
                  <SelectContent>
                    {owners.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Refresh owners"
                  title="Refresh owners from GitHub"
                  disabled={loadingOwners}
                  onClick={() => void loadOwners(true)}
                >
                  <RefreshCw
                    className={cn("h-4 w-4", loadingOwners && "animate-spin")}
                  />
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="repo-filter">Repository</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="repo-filter"
                  value={filter}
                  placeholder={owner ? "Search repositories…" : "Pick an owner first"}
                  disabled={!owner || loadingRepos}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-7"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                aria-label="Refresh repositories"
                title="Refresh repositories for this owner from GitHub"
                disabled={!owner || loadingRepos}
                onClick={() => void loadRepos(owner, true)}
              >
                <RefreshCw
                  className={cn("h-4 w-4", loadingRepos && "animate-spin")}
                />
              </Button>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-card">
              {loadingRepos && (
                <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading repositories…
                </div>
              )}
              {reposError && (
                <div className="px-2 py-2 text-sm text-destructive">{reposError}</div>
              )}
              {!loadingRepos && !reposError && filtered.length === 0 && (
                <div className="px-2 py-2 text-sm text-muted-foreground">
                  {owner
                    ? filter
                      ? "No matches"
                      : "No repositories accessible"
                    : "Select an owner to load repositories"}
                </div>
              )}
              {filtered.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRepo(r)}
                  className={cn(
                    "block w-full px-2 py-1.5 text-left text-sm hover:bg-muted",
                    repo === r && "bg-muted font-medium",
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canAdd} onClick={submit}>
            {submitting ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Adding…
              </span>
            ) : (
              "Add"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function humanError(e: unknown): string {
  if (e instanceof Error) {
    return e.message.replace(/^Error:\s*/i, "");
  }
  return String(e);
}
